import "./App.css";
import { useState, useCallback } from "react";
import SimpleMDE from "react-simplemde-editor";
import "easymde/dist/easymde.min.css";
import { useMemo, useEffect } from "react";
import {
  HighlightOutlined,
  ExportOutlined,
  PullRequestOutlined,
} from "@ant-design/icons";
import ImprovementPanel from "./ImprovementPanel";
import { Modal, Tooltip } from "antd";
import { StringDiff, DiffMethod } from "react-string-diff";

import logo from "./wizard.png";

const DEFAULT_PARAMS = {
  temperature: 0.3,
  max_tokens: 1000,
  top_p: 1,
  frequency_penalty: 0,
  presence_penalty: 0,
};

function App() {
  const [value, setValue] = useState("");
  const [working, setWorking] = useState(false);
  const [workbench, setWorkbench] = useState(false);
  const [panels, setPanels] = useState([]);
  const [openImproveModal, setOpenImproveModal] = useState(false);
  const [oldText, setOldText] = useState("");
  const [newText, setNewText] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [useGPT4, setUseGPT4] = useState(false);

  const onChange = useCallback((value) => {
    setValue(value);
  }, []);

  // useEffect to check if user is not on Chrome or Chromium
  useEffect(() => {
    if (!navigator.userAgent.includes("Chrome")) {
      alert(
        "This app is optimized for Chromium-based browsers. Please use one of these browsers for the best experience."
      );
    }
  }, []);

  const queryEditsPrompt = (userInput) => {
    document.body.style.cursor = "wait";
    setWorking(true);

    fetch("prompts/edits.prompt")
      .then((response) => response.text())
      .then((text) => text.replace("$essay", userInput))
      .then((prompt) => {
        console.log(prompt);

        const params = {
          ...DEFAULT_PARAMS,
        };

        if (useGPT4) {
          params["model"] = "gpt-4";
          params["messages"] = [{ role: "user", content: prompt }];
        } else {
          params["model"] = "text-davinci-003";
          params["prompt"] = prompt;
        }

        const requestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + String(apiKey),
          },
          body: JSON.stringify(params),
        };
        console.log(requestOptions);
        var url = "https://api.openai.com/v1/completions";
        if (useGPT4) {
          url = "https://api.openai.com/v1/chat/completions";
        }
        fetch(url, requestOptions)
          .then((response) => {
            if (!response.ok) {
              switch (response.status) {
                case 401: // 401: Unauthorized: API key is wrong
                  throw new Error("Please double-check your API key.");
                case 429: // 429: Too Many Requests: Need to pay
                  throw new Error(
                    "You exceeded your current quota, please check your plan and billing details."
                  );
                default:
                  throw new Error(
                    "Something went wrong with the request, please check the Network log"
                  );
              }
            }
            return response.json();
          })
          .then((response) => {
            console.log(response);
            var text = "";
            if (useGPT4) {
              const { choices } = response;
              text = choices[0].message.content;
            } else {
              const { choices } = response;
              text = choices[0].text;
            }

            const updates = JSON.parse(text);
            console.log(updates);
            updates["unchanged"] = userInput;

            // add to front of array
            panels.unshift(updates);

            setWorkbench(true);
            document.body.style.cursor = "default";
            setWorking(false);
          })
          .catch((error) => {
            console.log(error);
            alert(error);

            document.body.style.cursor = "default";
            setWorking(false);
          });
      });
  };

  const queryMagicFix = (userInput, improvements) => {
    document.body.style.cursor = "wait";
    setWorking(true);

    fetch("prompts/magicfix.prompt")
      .then((response) => response.text())
      .then((text) => text.replace("$essay", userInput))
      .then((text) => text.replace("$improvements", improvements))
      .then((prompt) => {
        console.log(prompt);

        const params = {
          ...DEFAULT_PARAMS,
        };

        if (useGPT4) {
          params["model"] = "gpt-4";
          params["messages"] = [{ role: "user", content: prompt }];
        } else {
          params["model"] = "text-davinci-003";
          params["prompt"] = prompt;
        }

        const requestOptions = {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + String(apiKey),
          },
          body: JSON.stringify(params),
        };
        console.log(requestOptions);
        var url = "https://api.openai.com/v1/completions";
        if (useGPT4) {
          url = "https://api.openai.com/v1/chat/completions";
        }
        fetch(url, requestOptions)
          .then((response) => {
            if (!response.ok) {
              switch (response.status) {
                case 401: // 401: Unauthorized: API key is wrong
                  throw new Error("Please double-check your API key.");
                case 429: // 429: Too Many Requests: Need to pay
                  throw new Error(
                    "You exceeded your current quota, please check your plan and billing details."
                  );
                default:
                  throw new Error(
                    "Something went wrong with the request, please check the Network log"
                  );
              }
            }
            return response.json();
          })
          .then((response) => {
            var text = "";
            if (useGPT4) {
              const { choices } = response;
              text = choices[0].message.content;
            } else {
              const { choices } = response;
              text = choices[0].text;
            }

            console.log(text);

            // show modal with diff
            setNewText(text);
            setOldText(userInput);
            setOpenImproveModal(true);

            document.body.style.cursor = "default";
            setWorking(false);
          })
          .catch((error) => {
            console.log(error);
            alert(error);

            document.body.style.cursor = "default";
            setWorking(false);
          });
      });
  };

  const exportToMarkdown = () => {
    const element = document.createElement("a");
    const file = new Blob([value], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = "your-awesome-essay.md";
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();

    // remove the element
    document.body.removeChild(element);
  };

  const toggleWorkbench = () => {
    setWorkbench(!workbench);
  };

  const analyzeHighlightedText = () => {
    if (apiKey === "" || apiKey === undefined) {
      alert("Please enter an API key");
      return;
    }

    const highlightedText = window.getSelection().toString();
    console.log(highlightedText);

    console.log("HI");

    if (highlightedText.length == 0) {
      alert(
        "Please highlight some text to analyze. If text is highlighted, please re-highlight it."
      );
      return;
    }

    queryEditsPrompt(highlightedText);

    window.getSelection().removeAllRanges();
  };

  const removePanelAtIndex = (index) => {
    panels.splice(index, 1);
    setPanels([...panels]);

    if (panels.length == 0) {
      setWorkbench(false);
    }
  };

  const improvePanelAtIndex = (index, panelText) => {
    if (apiKey === "" || apiKey === undefined) {
      alert("Please enter an API key");
      return;
    }

    if (value.indexOf(panelText) == -1) {
      alert(
        "Panel is out of date with the current essay, so cannot magic fix. Re-highlight text and run another analysis!"
      );
      return;
    }

    const panel = panels[index];
    const improvements = panel.improvements;

    setModalTitle(panel.title);
    queryMagicFix(panel.unchanged, improvements);
  };

  const mdeOptions = useMemo(() => {
    return {
      autofocus: true,
      spellChecker: true,
      hideIcons: ["fullscreen", "preview", "side-by-side", "guide"],
    };
  }, []);

  const handleOk = () => {
    // update value
    setValue(value.replace(oldText, newText));
    setOpenImproveModal(false);
  };

  const handleCancel = () => {
    setOpenImproveModal(false);
  };

  return (
    <div className="App">
      <Modal
        open={openImproveModal}
        title={'Fixes for "' + modalTitle + '"'}
        onOk={handleOk}
        onCancel={handleCancel}
        footer={[
          <button
            type="button"
            className="inline-flex mr-2 items-center rounded-full bg-red-500 text-white border px-4 py-2 text-md font-semibold shadow-md hover:bg-red-600 focus:outline-none"
            onClick={handleCancel}
          >
            Cancel
          </button>,
          <button
            type="button"
            className="inline-flex items-center rounded-full bg-green-600 px-4 py-2 text-md font-semibold text-white shadow-lg hover:bg-green-700 focus:outline-none"
            onClick={handleOk}
          >
            Merge{" "}
            <PullRequestOutlined
              style={{ verticalAlign: "1px", marginLeft: "5px" }}
            />
          </button>,
        ]}
      >
        <StringDiff
          oldValue={oldText}
          newValue={newText}
          method={DiffMethod.Words}
        />
      </Modal>
      <div className="flex flex-row justify-between bg-amber-50 py-4 px-8 items-center">
        <div className="flex flex-row space-x-2 items-center">
          <a className="font-bold text-3xl header-title text-gray-900" href="/">
            merlin
          </a>
          <img className="h-8 " src={logo} alt="profile" />
        </div>
        <div className="flex flex-row space-x-4 items-center hidden md:flex">
          <input
            type="text"
            placeholder="Add Your API Key"
            onChange={(e) => setApiKey(e.target.value)}
            className="rounded-sm bg-gray-100 px-4 py-2 text-md font-semibold text-gray-900 shadow-lg border-2 focus:outline-none"
          />
          <div className="flex flex-row items-center">
            <Tooltip
              title="You need GPT-4 access to use this. Keep this off for faster results."
              placement="bottom"
            >
              <input
                type="checkbox"
                id="gpt-4"
                name="gpt-4"
                value="gpt-4"
                checked={useGPT4}
                className="mr-2"
                onChange={(e) => setUseGPT4(e.target.checked)}
              />
              <label htmlFor="gpt-4" className="text-gray-900 font-semibold">
                GPT-4
              </label>
            </Tooltip>
          </div>
          <button
            type="button"
            onClick={exportToMarkdown}
            className="inline-flex items-center rounded-full bg-gray-900 px-4 py-2 text-md font-semibold text-white shadow-lg hover:bg-gray-700 focus:outline-none"
          >
            export .md &nbsp;
            <ExportOutlined />
          </button>
          {/* <button
            type="file"
            className="inline-flex items-center rounded-full bg-blue-900 px-4 py-2 text-md font-semibold text-white shadow-lg hover:bg-blue-700 focus:outline-none"
          >
            doc upload &nbsp;
            <FileAddOutlined />
          </button> */}
          <button
            type="button"
            onClick={analyzeHighlightedText}
            className="inline-flex items-center rounded-full bg-indigo-900 px-4 py-2 text-md font-semibold text-white shadow-lg hover:bg-indigo-700 focus:outline-none"
          >
            analyze &nbsp;
            <HighlightOutlined />
          </button>
          {/* <button
            type="button"
            className="inline-flex items-center border rounded-full bg-slate-100 px-4 py-2 text-md font-semibold text-white shadow-lg hover:bg-slate-200 focus:outline-none"
          >
            <SettingOutlined className="text-gray-900" />
          </button> */}
          {panels.length > 0 && (
            <button
              type="button"
              onClick={toggleWorkbench}
              className="inline-flex items-center rounded-full bg-gray-900 px-4 py-2 text-md font-semibold text-white shadow-lg hover:bg-gray-700 focus:outline-none"
            >
              ◨
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-row">
        <div
          className={
            !working
              ? "flex flex-col w-full border border-gray-300 bg-white rounded-lg shadow-lg ml-10 mr-10 my-3 mb-10 p-4 h-fit"
              : "flex flex-col w-full border border-gray-300 bg-white rounded-lg shadow-lg ml-10 mr-10 my-3 mb-10 p-4 h-fit disabled-editor"
          }
        >
          <SimpleMDE options={mdeOptions} value={value} onChange={onChange} />
        </div>
        {workbench && (
          <div className="w-1/3">
            {panels.map((panel, index) => {
              return (
                <ImprovementPanel
                  key={index}
                  index={index}
                  summary={panel.summary}
                  removalFunc={removePanelAtIndex}
                  improvementFunc={improvePanelAtIndex}
                  improvements={panel.improvements}
                  questions={panel.questions}
                  remarks={panel.remarks}
                  text={panel.unchanged}
                  title={panel.title}
                />
              );
            })}
          </div>
        )}
      </div>
      <p className="text-amber-900 text-center italic text-sm pb-10">
        "You must never think at the typewriter – you must feel. Your intellect
        is always buried in that feeling anyway." - Ray Bradbury
      </p>
    </div>
  );
}

export default App;
